// Supprime les imports directs, 'api' sera injecté.
// import apiService from "../api/index.js";
// import { handleApiError, generateFallbackData } from "../utils/errorHandler";

// Liste des devises supportées par la plateforme
const SUPPORTED_CURRENCIES = ["XOF", "EUR", "USD", "BBT", "GBP", "NGN", "GHS"];

export const createWalletService = (api) => {
  const baseUrl = "/wallet";
  
  return {
    async getBalance() {
      try {
        const response = await api.get(`${baseUrl}/balance`);
        
        // Vérifier si toutes les devises nécessaires sont présentes
        if (response.data && response.data.balances) {
          const missingCurrencies = this.checkMissingCurrencies(response.data.balances);
          
          // S'il y a des devises manquantes, les initialiser automatiquement
          if (missingCurrencies.length > 0) {
            await this.initializeMissingCurrencies();
            
            // Récupérer à nouveau les soldes mis à jour
            const updatedResponse = await api.get(`${baseUrl}/balance`);
            return updatedResponse.data;
          }
        }
        
        return response.data;
      } catch (error) {
        console.error("Erreur (getBalance):", error);
        // Retourner une structure de données de secours
        return { balances: SUPPORTED_CURRENCIES.map(c => ({ currency: c, balance: 0, locked: 0 })) };
      }
    },

    /**
     * Vérifie les devises manquantes dans le portefeuille de l'utilisateur
     * @param {Array} balances Tableau des soldes actuels
     * @returns {Array} Liste des devises manquantes
     */
    checkMissingCurrencies(balances) {
      const currentCurrencies = balances.map(balance => balance.currency);
      return SUPPORTED_CURRENCIES.filter(currency => !currentCurrencies.includes(currency));
    },

    /**
     * Appelle le backend pour s'assurer que toutes les devises sont initialisées pour l'utilisateur.
     * @returns {Promise<Object>} Résultat de l'initialisation
     */
    async initializeMissingCurrencies() {
      try {
        const response = await api.post(`${baseUrl}/initialize-currencies`, {});
        console.log('Initialisation des devises vérifiée/effectuée avec succès via /initialize-currencies');
        return response.data;
      } catch (error) {
        console.error("Erreur lors de l'appel à /initialize-currencies:", error);
        return { success: false, message: "Échec de l'initialisation des devises manquantes." };
      }
    },

    async getTransactions(params = { limit: 10, page: 1 }) {
      try {
        const response = await api.get(`${baseUrl}/transactions`, {
          params,
        });
        return response.data;
      } catch (error) {
        console.error("Erreur lors de la récupération des transactions:", error);
        return {
          transactions: [],
          stats: {
            totalCount: 0,
            pendingCount: 0,
            totalConverted: 0,
          },
        };
      }
    },

    async initializeWallet(userId, currency = "XOF") {
      try {
        const response = await api.post(`${baseUrl}/initialize`, {
          currency,
        });
        
        await this.initializeMissingCurrencies();
        
        return response.data;
      } catch (error) {
        console.error("Erreur lors de l'initialisation du portefeuille:", error);

        if (error.response?.status === 429) {
          throw error;
        }

        // Retourner un objet d'erreur simple
        return {
          error: true,
          message: "Erreur lors de l'initialisation du portefeuille",
          wallet: {
            currency,
            balance: 0,
          },
        };
      }
    },

    /**
     * Vérifie et corrige la validité du portefeuille
     * @returns {Promise<Object>} Statut de la vérification
     */
    async validateWallet() {
      try {
        const balanceData = await this.getBalance();
        
        if (balanceData.error === "WALLET_VALIDATION_FAILED") {
          console.log("Problème de validation du portefeuille détecté, tentative de correction...");
          
          await this.initializeMissingCurrencies();
          
          return {
            success: true,
            message: "Portefeuille corrigé avec succès"
          };
        }
        
        return {
          success: true,
          message: "Portefeuille valide"
        };
      } catch (error) {
        console.error("Erreur lors de la validation du portefeuille:", error);
        return {
          success: false,
          message: "Impossible de valider le portefeuille",
          error: error.message
        };
      }
    },

    /**
     * Initie une demande de dépôt.
     * @param {Object} depositData - Données du dépôt (amount, currency, paymentMethod).
     * @returns {Promise<Object>} Réponse de l'API.
     */
    async initiateDeposit(depositData) {
      try {
        const response = await api.post(`${baseUrl}/deposit`, depositData);
        return response.data;
      } catch (error) {
        console.error("Erreur lors de l'initiation du dépôt:", error);
        throw error;
      }
    },

    /**
     * Initie une demande de retrait.
     * @param {Object} withdrawalData - Données du retrait (amount, currency, paymentMethod, withdrawalDetails).
     * @returns {Promise<Object>} Réponse de l'API.
     */
    async initiateWithdrawal(withdrawalData) {
      try {
        const response = await api.post(`${baseUrl}/withdraw`, withdrawalData);
        return response.data;
      } catch (error) {
        console.error("Erreur lors de l'initiation du retrait:", error);
        throw error;
      }
    },
  };
};
